import { extend } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import Badge from 'flarum/common/components/Badge';
import Button from 'flarum/common/components/Button';
import Discussion from 'flarum/common/models/Discussion';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';

import ImageLightboxModal from './components/ImageLightboxModal';
import VisibilityModal from './components/VisibilityModal';
import VisibilitySelector from './components/VisibilitySelector';
import configureWelcomeHeroDismissal from './configureWelcomeHeroDismissal';
import configurePostTimestampDisplay from './configurePostTimestampDisplay';

function lightboxSource(image) {
  if (!image.naturalWidth || !image.naturalHeight) {
    return image.currentSrc || image.src;
  }

  try {
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    canvas.getContext('2d').drawImage(image, 0, 0);

    return canvas.toDataURL('image/png');
  } catch (error) {
    return image.currentSrc || image.src;
  }
}

function exportFilename(response, discussion) {
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="([^"]+)"/i);

  return match ? match[1] : `discussion-${discussion.id()}.txt`;
}

function exportDiscussion(discussion) {
  return fetch(`${app.forum.attribute('apiUrl')}/discussions/${discussion.id()}/export.txt`, {
    credentials: 'same-origin',
    headers: {
      'X-CSRF-Token': app.session.csrfToken,
    },
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`Export request failed with status ${response.status}`);
    }

    return response.blob().then((blob) => {
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = downloadUrl;
      link.download = exportFilename(response, discussion);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
    });
  });
}

app.initializers.add('jcswa-discussion-visibility', () => {
  configureWelcomeHeroDismissal();
  configurePostTimestampDisplay();

  Discussion.prototype.isPrivate = Discussion.attribute('isPrivate');
  Discussion.prototype.canSetVisibility = Discussion.attribute('canSetVisibility');

  extend(Discussion.prototype, 'badges', function (badges) {
    if (this.isPrivate() && !badges.has('private')) {
      badges.add(
        'private',
        <Badge type="private" icon="fas fa-lock" label={app.translator.trans('jcswa-discussion-visibility.forum.badge.private_tooltip')} />
      );
    }
  });

  extend(DiscussionComposer.prototype, 'oninit', function () {
    if (typeof this.composer.fields.isPrivate === 'undefined') {
      this.composer.fields.isPrivate = true;
    }
  });

  extend(DiscussionComposer.prototype, 'headerItems', function (items) {
    items.add(
      'visibility',
      <VisibilitySelector
        isPrivate={this.composer.fields.isPrivate !== false}
        disabled={!!this.attrs.disabled}
        onchange={(isPrivate) => {
          this.composer.fields.isPrivate = isPrivate;
        }}
      />,
      5
    );
  });

  extend(DiscussionComposer.prototype, 'data', function (data) {
    data.isPrivate = this.composer.fields.isPrivate !== false;
  });

  extend(DiscussionControls, 'moderationControls', function (items, discussion) {
    if (discussion.canSetVisibility()) {
      items.add(
        'visibility',
        <Button icon={discussion.isPrivate() ? 'fas fa-lock' : 'fas fa-globe'} onclick={DiscussionControls.visibilityAction.bind(discussion)}>
          {app.translator.trans('jcswa-discussion-visibility.forum.discussion_controls.change_visibility_button')}
        </Button>
      );
    }
  });

  extend(DiscussionControls, 'userControls', function (items, discussion, context) {
    if (!app.session.user || !(context instanceof DiscussionPage)) {
      return;
    }

    items.add(
      'export',
      <Button
        icon="fas fa-file-export"
        onclick={() =>
          exportDiscussion(discussion).catch(() => {
            app.alerts.show({ type: 'error' }, app.translator.trans('jcswa-discussion-visibility.forum.discussion_controls.export_failed'));
          })
        }
      >
        {app.translator.trans('jcswa-discussion-visibility.forum.discussion_controls.export_button')}
      </Button>
    );
  });

  DiscussionControls.visibilityAction = function () {
    app.modal.show(VisibilityModal, { discussion: this });
  };

  document.addEventListener('click', (event) => {
    if (event.button !== 0 || event.defaultPrevented) {
      return;
    }

    const image = event.target.closest('.PostStream .FoFUpload--Upl-Image-Preview');

    if (!image) {
      return;
    }

    event.preventDefault();
    app.modal.show(ImageLightboxModal, {
      src: lightboxSource(image),
      alt: image.alt || '',
    });
  });
});
